import { Config } from "../core/config/config";
import { Grid } from "../core/components/grid";
import { HTTPService } from "../core/util/httpManager";
import { Signature, ReportDefinition } from "./reportMetaData";
import { ToolBarStandardButtonType, ToolBar, ToolBarStandardButton } from "../core/components/toolBar";
import EditSignature, { SignatureEntity, EditSignatureProps } from "./editSignature";
import { Helper } from "../core/util/helper";
import { SheetEventType } from "../../siteManagement/sheet";
import { General } from "../../siteManagement/general";
import { ReportMetaDataHelper, Language } from "./reportMetaDataHelper";
import { WellKnownSigneeRole } from "./editRole";
import { DialogHelper } from "../core/util/dialogHelper";
import { Metadata } from "../core/components/commonApp";
import { KeyValuePair } from "../commonInterfaces";
import React from "react";

export default class SignatureGrid extends React.Component<SignatureGridProps, SignatureGridState> {
    private gridOptions: kendo.ui.GridOptions;
    private roles: RoleViewModel[];
    private defaultReasonsObject;
    constructor(props: SignatureGridProps) {
        super(props);
        this.roles = [];
        this.state = {
            gridData: [],
            selected: null
        }
        this.initApp();
    }

    private onChange = () => {
        let lst: Signature[] = this.state.gridData.map((item) => {
            return {
                Properties: item.Properties,
                Reasons: (item.Reasons || this.defaultReasonsObject),
                Role: item.Role,
                UniqueName: item.UniqueName
            };
        })
        this.props.onChange(this.props.field, lst);
    }

    private initApp = () => {
        this.gridOptions = {
            sortable: true,
            pageable: true,
            groupable: false,
            filterable: false,
            selectable: true,
            resizable: true,
            scrollable: true,
            columns: [
                {
                    title: this.props.context.translations["WebSMS.Ticket.Allocations.Role"],
                    field: "RoleName"
                },
                {
                    title: this.props.context.translations["WebSMS.ReportMetadata.Reason"],
                    field: "DisplayReason"
                },
                {
                    title: this.props.context.translations["WebSMS.ReportMetadata.UniqueName"],
                    field: "UniqueName"
                }
            ]
        };
        ReportMetaDataHelper.getLanguages().then((languages: Language[]) => {
            let obj = {};
            languages.forEach((lang) => {
                obj[lang.Code] = null;
            })
            this.defaultReasonsObject = obj;
        })
        

        HTTPService.get("ReportMetaData/GetAllRoles").then((result) => {
            this.roles = result;
            let gridData: SigantureGridData[] = [];
            let index = -1;
            for (let i = 0; i < this.props.value.length; i++) {
                index = this.roles.findIndex(f => f.Key === this.props.value[i].Role);
                gridData.push({
                    GridId: kendo.guid(),
                    RoleName: index >= 0 ? this.roles[index].Value : null,
                    Properties: this.props.value[i].Properties,
                    Reasons: this.props.value[i].Reasons,
                    Role: this.props.value[i].Role,
                    DisplayReason: ReportMetaDataHelper.getDisplayText(this.props.value[i].Reasons),
                    UniqueName: this.props.value[i].UniqueName
                });
            }
            this.setState({ gridData: gridData })
        })

    }

    private createGrid = () => {

        let standardButtons: ToolBarStandardButton[] = [];
        if (!this.props.disabled) {
            standardButtons = [
                { type: ToolBarStandardButtonType.NEW, caption: Config.translations.add, enabled: true },
                { type: ToolBarStandardButtonType.EDIT, caption: Config.translations.editLabel, enabled: Helper.hasValue(this.state.selected) },
                { type: ToolBarStandardButtonType.DELETE, caption: Config.translations.deleteLabel, enabled: Helper.hasValue(this.state.selected) }
            ];
        }

        const body = this.state.gridData.length > 0 ? (
            <>
                <Grid
                    data={this.state.gridData}
                    options={this.gridOptions}
                    id="signatureGrid"
                    onSelectionChanged={this.onSelectionChange}
                />
            </>
        ) : this.createReplacementText();
        return (
            <div className="flex-container">
                <ToolBar standardButtons={standardButtons} handleStandardButtonClick={this.onStandardClick} />
                {body}
            </div>
        );
    }

    private createReplacementText = () => {
        return (
            <div style={{ display: "grid" }}>
                <div style={{ display: "flex", justifyContent: "center", gridArea: "1/1/1/1" }}>
                    <i className={`fal fa-signature fa-10x`} style={{ opacity: 0.1 }}></i>
                </div>
                <div style={{ textAlign: "center", gridArea: "1/1/1/1", paddingTop: "40px" }}>
                    <label style={{ fontSize: "15px" }}>
                        {this.props.context.translations["WebSMS.ReportMetadata.NoSignatureAvailable"]}
                    </label>
                </div>
            </div>
        );
    }

    private onStandardClick = async (type: ToolBarStandardButtonType) => {
        const dataItem: SigantureGridData = this.state.selected;
        switch (type) {
            case ToolBarStandardButtonType.NEW:
                this.openSheet(null, false);
                break;
            case ToolBarStandardButtonType.EDIT:
                this.openSheet(dataItem, false);
                break;
            case ToolBarStandardButtonType.DELETE:
                let gridData = this.state.gridData.filter(f => f.GridId !== dataItem.GridId);
                const isOk: boolean = await DialogHelper.choose(Config.translations.deleteMessage, [{ label: Config.translations.ok, value: true, primary: true }], true);
                if (Helper.hasValue(isOk) && isOk) {
                    this.setState({ gridData: gridData, selected: null }, this.onChange)
                }
                break;
        }
    }

    private onSelectionChange = (dataItem: SigantureGridData) => {
        if (this.props.disabled) {
            this.openSheet(dataItem, true);
        } else {
            this.setState({ selected: dataItem })
        }
    }

    private getBool = (properties: {}, field: string) => {
        let mandatory = (Helper.getAttributeSavely(properties, field) as string)?.toString();
        if (Helper.isNullOrEmpty(mandatory))
            return false;
        mandatory = mandatory.trim().toLowerCase();
        return mandatory == "1" || mandatory == "true";
    }

    private openSheet = (dataItem: SigantureGridData, readOnly: boolean) => {
        const getReasons = (obj: {}) => {
            let result = {};
            const ignoredKeys = [
                "_events", "_handlers", "uid", "parent"
            ];
            let keys = Object.keys(obj);
            for (let i = 0; i < keys.length; i++) {
                let index = ignoredKeys.findIndex(f => f == keys[i]);
                if (index == -1) {
                    result[keys[i]] = obj[keys[i]];
                }
            }
            return result;
        }
        let entity: SignatureEntity = {
            Top: Helper.hasValue(dataItem) ? Helper.getAttributeSavely(dataItem.Properties, "TOP") : null,
            Left: Helper.hasValue(dataItem) ? Helper.getAttributeSavely(dataItem.Properties, "LEFT") : null,
            Height: Helper.hasValue(dataItem) ? Helper.getAttributeSavely(dataItem.Properties, "HEIGHT") : null,
            Width: Helper.hasValue(dataItem) ? Helper.getAttributeSavely(dataItem.Properties, "WIDTH") : null,
            Page: Helper.hasValue(dataItem) ? Helper.getAttributeSavely(dataItem.Properties, "PAGE") : -1,
            Mandatory: Helper.hasValue(dataItem) ? this.getBool(dataItem.Properties, "MANDATORY") : false,
            AdditionalCompanyField: Helper.hasValue(dataItem) ? this.getBool(dataItem.Properties, "ADDITIONALCOMPANYFIELD") : false,
            Role: Helper.hasValue(dataItem) ? dataItem.Role : null,
            GridId: Helper.hasValue(dataItem) ? dataItem.GridId : kendo.guid(),
            IsNew: !Helper.hasValue(dataItem),
            Reasons: Helper.hasValue(dataItem) ? getReasons(dataItem.Reasons) : {},
            UniqueName: Helper.hasValue(dataItem) ? dataItem.UniqueName : "",
            WellKnownSigneeRole: Helper.hasValue(dataItem) ? dataItem.WellKnownSigneeRole : null,
            RecordRight: {
                CanRead: true,
                CanWrite: !readOnly
            },
            IndividualFields: null,
            IsWatchable: false,
            IsWatched: false,
            DefineByQuickReport: null
        };
        entity.DefineByQuickReport = this.isDefinedByQuickReport(entity);
        HTTPService.chainBatch
            .add<Metadata>(Helper.getMetaData("editSignature"))
            .promise()
            .then((result) => {
                const props: EditSignatureProps = {
                    isNameUnique: this.isNameUnique,
                    context: {
                        contextType: "editSignature",
                        usedRoles: this.state.gridData.map((item) => { return item.Role })
                    },
                    title: "",
                    metadata: result[0],
                    entity: entity,
                    dataSources: {
                        roles: this.roles
                    },
                    uniqueNames: {
                        customer: this.getDefaultUniqueName(WellKnownSigneeRole.Customer),
                        dispatcher: this.getDefaultUniqueName(WellKnownSigneeRole.Dispatcher),
                        technician: this.getDefaultUniqueName(WellKnownSigneeRole.Technician),
                        other: this.getDefaultUniqueName()
                    },
                    isWindow: false
                };
                let sheet = General.Sheet.Manager.createSheet()
                sheet.show(() => {
                    sheet.mountContent(EditSignature, props);
                });
                sheet.bind(SheetEventType.SavedSuccessfully, this.onSave)
            })
    }

    private getDefaultUniqueName = (wellKnownSigneeRole?: WellKnownSigneeRole) => {
        const dict = {
            1: "Technician",
            2: "Customer",
            3: "Dispatcher"
        }
        let id: string = null;
        let tmp: string = null;
        let count = 1;
        while (Helper.isNullOrEmpty(id)) {
            tmp = (Helper.hasValue(wellKnownSigneeRole) ? dict[wellKnownSigneeRole] : "Signatur") + "-" + count.toString();
            if (this.isNameUnique(tmp)) {
                id = tmp;
            } else {
                count = count + 1;
            }
        }
        return id;
    }

    private isDefinedByQuickReport = (entity: SignatureEntity) => {
        return !(Helper.hasValue(entity.Left) && Helper.hasValue(entity.Top) && Helper.hasValue(entity.Height) && Helper.hasValue(entity.Width));
    }

    private isNameUnique = (name: string): boolean => {
        return this.state.gridData.filter(f => f.UniqueName === name).length === 0;
    }

    private onSave = (data: { entity: SignatureEntity }) => {
        let obj = {
            "MANDATORY": data.entity.Mandatory,
            "ADDITIONALCOMPANYFIELD": data.entity.AdditionalCompanyField
        };
        if (!data.entity.DefineByQuickReport) {
            obj["TOP"] = data.entity.Top;
            obj["LEFT"] = data.entity.Left;
            obj["HEIGHT"] = data.entity.Height;
            obj["WIDTH"] = data.entity.Width;
            obj["PAGE"] = data.entity.Page;
        }

        let gridData = this.state.gridData;
        let index = gridData.findIndex(f => f.GridId === data.entity.GridId);
        let roleName = this.roles.find(f => f.Key === Helper.getValueOrDefault(data.entity.Role, 1)).Value;
        if (index >= 0) {
            gridData[index] = {
                RoleName: roleName,
                Properties: obj,
                Reasons: data.entity.Reasons,
                Role: data.entity.Role,
                GridId: data.entity.GridId,
                DisplayReason: ReportMetaDataHelper.getDisplayText(data.entity.Reasons),
                UniqueName: data.entity.UniqueName,
                WellKnownSigneeRole: data.entity.WellKnownSigneeRole
            };
        } else {
            gridData.push({
                RoleName: roleName,
                Properties: obj,
                Reasons: data.entity.Reasons,
                Role: data.entity.Role,
                GridId: data.entity.GridId,
                DisplayReason: ReportMetaDataHelper.getDisplayText(data.entity.Reasons),
                UniqueName: data.entity.UniqueName,
                WellKnownSigneeRole: data.entity.WellKnownSigneeRole
            });
        }
        this.setState({ gridData: gridData }, this.onChange)
    }

    render() {
        return <>{this.createGrid()}</>;
    }

}
interface SignatureGridProps {
    entity: ReportDefinition;
    value: Signature[];
    onChange: (field, value) => void;
    field: any;
    disabled: boolean;
    context: {
        translations: {}
    }
}
interface SignatureGridState {
    gridData: SigantureGridData[];
    selected: SigantureGridData;
}
interface SigantureGridData extends Signature {
    RoleName: string;
    DisplayReason: string;
    GridId: string;
}
export interface RoleViewModel extends KeyValuePair<number, string> {
    WellKnownSigneeRole?: WellKnownSigneeRole
}

